import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { WorkflowContextService, WorkflowService } from 'src/app/services';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from '../../filter-list/models/filterClasses';
import { Subscription, of } from 'rxjs';
import {
  FilterBuilderParamOpts,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import { myWorkOptions } from '../../common/common-filterbuilder-staff-janus/common-filterbuilder-staff-janus.model';
import { RenewalsJanusService } from './renewals-janus.service';
import { ToastrService } from 'ngx-toastr';
import { ComponentIds } from 'src/app/models/component-ids';
import { ColumnOption } from 'src/app/models/grid-settings';
import { TableColumn } from '../../common/common-table/common-table.component';
import { CommonFilterbuilderStaffJanusComponent } from '../../common/common-filterbuilder-staff-janus/common-filterbuilder-staff-janus.component';
import { ScheduledExportOverlayService } from 'src/app/views/reports-janus/reports-table-header/scheduled-exports-janus/scheduled-exports-overlayh.service';

type RenewalsSharedState = {
  searchText: ItemSearchOptionField[];
  filterInputs: ItemSearchOptionField[];
  attributeFilterInputs: ItemSearchOptionField[];
  sortField: string | null;
  sortDescending: boolean;
  searchQuery?: string;
};

type RenewalsSharedRouteOption = {
  t?: string;
  f?: string;
  s?: string;
  op?: string;
  m?: string;
  i?: string;
  c?: { start?: string; end?: string };
};

type RenewalsSharedRouteFilter = {
  t: string;
  o: RenewalsSharedRouteOption[];
};

type RenewalsSharedRouteState = {
  q?: string;
  f?: RenewalsSharedRouteFilter[];
  a?: RenewalsSharedRouteFilter[];
  sf?: string;
  sd?: 0 | 1;
};

@Component({
  selector: 'wm-renewals-janus',
  standalone: false,
  templateUrl: './renewals-janus.component.html',
  styleUrls: ['./renewals-janus.component.scss']
})
export class RenewalsJanusComponent
  implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private workflowSvc: WorkflowService,
    private context: WorkflowContextService,
    private route: ActivatedRoute,
    public router: Router,
    private _toastrSvc: ToastrService,
    private renewalsJanusSvc: RenewalsJanusService,
    private ref: ChangeDetectorRef,
    private scheduledExportOverlaySvc: ScheduledExportOverlayService
  ) {}

  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-renewals-janus';
  renewals: any[] = [];
  currentPage = 0;
  pageSize = 10;
  totalPages = 0;
  totalRecords = 0;
  isLoading = false;
  isInitialLoading = false;
  private dataSub?: Subscription;
  private departmentRouteSub?: Subscription;
  private renewalsStateSub?: Subscription;
  exportOptions: string[];
  private isDestroyed = false;
  @ViewChild(CommonFilterbuilderStaffJanusComponent)
  private filterBuilderComponent?: CommonFilterbuilderStaffJanusComponent;

  departments = ['BUILDINGS DEPARTMENT', 'PLANNING', 'ENVIRONMENT'];
  selectedDepartment = 'BUILDINGS DEPARTMENT';

  searchQuery = '';

  showDeptOptions = false;
  showAttributeOptions = false;
  showMyWorkOptions = false;
  showExportOptions = false;

  filterInputs: ItemSearchOptionField[] = [];
  workflowOpts: FilterBuilderParamOpts[] = [];
  applicationAttributes: FilterBuilderParam[] = [];
  filterOptions: ItemSearchOptionField[] = [];

  @Input() public sortField: string | null;
  @Input() public sortDescending = true;
  private readonly SORT_CACHE_KEY = 'datatable-renewals-application-sort';
  readonly FILTER_CACHE_KEY = 'renewals-janus-state';
  private readonly LEGACY_FILTER_CACHE_KEY = 'renewals-mywork-filters';
  private readonly SHARED_STATE_QUERY_PARAM = 'rs';
  private readonly SEARCH_QUERY_PARAM = 'q';
  private readonly ATTRIBUTE_FILTER_QUERY_PARAM = 'af';
  private readonly EXTERNAL_FILTER_QUERY_PARAM = 'ef';
  private readonly SORT_FIELD_QUERY_PARAM = 'sf';
  private readonly SORT_DESC_QUERY_PARAM = 'sd';
  private readonly DEFAULT_SORT_FIELD = 'registeredOn';
  private readonly DEFAULT_SORT_DESCENDING = true;
  private pendingSharedState: RenewalsSharedState | null = null;
  private lastSharedStateParam: string | null | undefined = undefined;
  private filtersReady = false;
  private pendingDeepLinkRegistrationId: string | null = null;
  private pendingDeepLinkView:
    | 'details'
    | 'application'
    | 'renew'
    | null = null;
  private resolvingDeepLinkPage = false;
  private resolvedDeepLinkRegistrationId: string | null = null;
  private failedDeepLinkRegistrationId: string | null = null;
  private currentDepartmentId: string | null = null;
  // Added by Amisha
  // Reason: Prevent page-1 flicker by resolving copied renewal deep links to the correct Janus page before rendering.
  private shouldResolveDeepLinkBeforeRender = false;

  isSorting = false;

  onFilterChanged(newFilters: ItemSearchOptionField[]) {
    const data = this.renewalsJanusSvc.getData();
    const attributeFilterInputs = this.cloneFilterInputs(newFilters);

    this.renewalsJanusSvc.setData({
      ...data,
      attributeFilterInputs,
      reRunJanusApplicationDataTable: true
    });
    this.syncSharedStateToRoute();
  }

  initFilters(): Promise<void> {
    const departmentId =
      this.currentDepartmentId || this.context.staffDepartmentId || undefined;
    const renewableWorkflows$ = this.context.client
      ? this.workflowSvc.getWorkflows(
          this.context.client,
          false,
          false,
          [4],
          departmentId
        )
      : of([]);

    return renewableWorkflows$.toPromise().then(renewableWorkflows => {
      this.workflowOpts = (renewableWorkflows || [])
        .map((workflow: any) => ({
          name: workflow.version?.name ?? workflow.name,
          value: workflow.id
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      this.updateApplicationAttributesFromColumns();
    });
  }

  myWorkOptions: myWorkOptions[] = [
    {
      name: 'Renewals About To Expire',
      templateId: 'expiringRenewals',
      displayOrder: 1,
      value: '<expiresOn',
      searchText: this.getSevenDaysFromNow()
    }
  ];

  onExternalFiltersChanged(updated: ItemSearchOptionField[]) {
    const filterInputs = this.cloneFilterInputs(updated);
    this.filterInputs = filterInputs;

    const existing = this.renewalsJanusSvc.getData();

    this.renewalsJanusSvc.setData({
      ...existing,
      filterInputs,
      reRunJanusApplicationDataTable: true
    });
    this.reloadTableWhenChipShows();
    this.syncSharedStateToRoute();
  }

  onSearch(value: ItemSearchOptionField[]) {
    const searchText = this.cloneFilterInputs(value);
    this.searchQuery = this.getSearchQuery(searchText);
    this.renewalsJanusSvc.setData({
      ...this.renewalsJanusSvc.getData(),
      searchText,
      reRunJanusApplicationDataTable: true
    });
    this.syncSharedStateToRoute();
  }

  getSevenDaysFromNow(): string {
    const d = new Date();
    d.setDate(d.getDate() + 7);

    return d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
  }

  private readonly REG_STATUS: Record<number, string> = {
    0: 'Pending',
    1: 'Approved',
    2: 'Denied',
    3: 'Expired',
    4: 'Inactive',
    5: 'Deleted',
    6: 'Renewed',
    7: 'Disabled'
  };

  id = ComponentIds.RENEWALS_SEARCH;
  title = 'Renewals Search';
  public useExcelFormat: boolean;
  public sort: { dir: string; prop: string } = {
    dir: 'desc',
    prop: 'registeredOn'
  };
  columns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  private visibleRenewalColumnKeys: string[] | null = null;
  staticFilters: ItemSearchOptionField[] = [];
  public page = {
    size: 10,
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0
  };

  onSortChanged(event) {
    if (
      this.sortField === event.sortField &&
      this.sortDescending === event.sortDescending
    )
      return;

    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.isSorting = true;

    localStorage.setItem(
      this.SORT_CACHE_KEY,
      JSON.stringify({
        sortField: this.sortField,
        sortDescending: this.sortDescending
      })
    );

    const data = this.renewalsJanusSvc.getData();

    this.renewalsJanusSvc.setData({
      ...data,
      reRunJanusApplicationDataTable: true
    });
    this.syncSharedStateToRoute();
  }

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }
    this.renewalsJanusSvc.setData({
      ...this.renewalsJanusSvc.getData(),
      searchText: [],
      filterInputs: [],
      attributeFilterInputs: [],
      reRunJanusApplicationDataTable: false
    });

    this.isLoading = true;
    this.isInitialLoading = true;

    localStorage.removeItem(this.SORT_CACHE_KEY);
    const sortCache = localStorage.getItem(this.SORT_CACHE_KEY);

    if (sortCache) {
      try {
        const parsed = JSON.parse(sortCache);

        this.sortField = parsed.sortField ?? 'registeredOn';
        this.sortDescending = parsed.sortDescending ?? true;
      } catch (e) {
        console.error('Invalid sort cache', e);
      }
    }

    this.scheduledExportOverlaySvc.saved$.subscribe(saved => {
      if (saved) {
        this.scheduledExportOverlaySvc.clearSaved();
      }
    });

    this.route.queryParamMap.subscribe(params => {
      const showAppView = params.get('showAppView');
      const pageParam = params.get('p');
      const page = Number(pageParam ?? 1);
      const registrationId = params.get('registrationId');
      const view = params.get('view') as
        | 'details'
        | 'application'
        | 'renew'
        | null;
      const sharedStateParam = params.get(this.SHARED_STATE_QUERY_PARAM);
      const normalizedPage = Number.isNaN(page) || page < 1 ? 1 : page;
      this.currentPage = normalizedPage - 1;
      this.pendingDeepLinkRegistrationId = registrationId;
      this.pendingDeepLinkView = view;

      if (!registrationId || !view) {
        this.resolvedDeepLinkRegistrationId = null;
        this.failedDeepLinkRegistrationId = null;
        this.resolvingDeepLinkPage = false;
        this.shouldResolveDeepLinkBeforeRender = false;
      } else if (registrationId !== this.resolvedDeepLinkRegistrationId) {
        this.resolvedDeepLinkRegistrationId = null;
        this.failedDeepLinkRegistrationId = null;

        if (!pageParam || this.shouldResolveDeepLinkBeforeRender) {
          this.shouldResolveDeepLinkBeforeRender = true;
        }
      }

      const parsedSharedState = this.parseRouteSharedState(params);
      const normalizedSharedQueryParams = this.buildSharedStateQueryParams(
        parsedSharedState
      );
      const routeStateSignature = this.getRouteStateSignature(params);

      if (
        !pageParam ||
        normalizedPage !== page ||
        this.shouldNormalizeSharedStateParams(
          params,
          normalizedSharedQueryParams,
          sharedStateParam
        )
      ) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            p: normalizedPage,
            ...normalizedSharedQueryParams
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }

      if (routeStateSignature !== this.lastSharedStateParam) {
        this.lastSharedStateParam = routeStateSignature;
        this.pendingSharedState = parsedSharedState;

        if (this.pendingSharedState) {
          this.applySharedState(this.pendingSharedState, this.filtersReady);
        }
      }

      if (showAppView === '1') {
        return;
      }
    });

    this.currentDepartmentId =
      this.route.parent?.snapshot.paramMap.get('departmentId') || null;

    this.initFilters().then(() => {
      this.filtersReady = true;

      const restoredSharedState = this.pendingSharedState;

      if (restoredSharedState) {
        this.pendingSharedState = restoredSharedState;
        this.applySharedState(restoredSharedState, false);
      } else {
        this.searchQuery = '';
        this.syncSearchQueryToFilterBuilder();
        localStorage.removeItem(this.FILTER_CACHE_KEY);

        const cache = localStorage.getItem(this.LEGACY_FILTER_CACHE_KEY);

        if (cache) {
          const parsed = JSON.parse(cache);

          this.filterInputs = parsed;

          this.renewalsJanusSvc.setData({
            ...this.renewalsJanusSvc.getData(),
            filterInputs: parsed
          });
        }
      }

      let departmentId = this.route.parent?.snapshot.paramMap.get(
        'departmentId'
      );
      let isFirstDepartmentEmission = true;
      this.departmentRouteSub = this.route.parent?.paramMap.subscribe(
        params => {
          const nextDepartmentId = params.get('departmentId');

          if (!nextDepartmentId) {
            return;
          }

          if (!isFirstDepartmentEmission && nextDepartmentId === departmentId) {
            return;
          }

          isFirstDepartmentEmission = false;
          departmentId = nextDepartmentId;
          this.currentDepartmentId = nextDepartmentId;
          this.initFilters().then(() => this.detectChanges());
          this.renewalsJanusSvc.setData({
            ...this.renewalsJanusSvc.getData(),
            reRunJanusApplicationDataTable: true
          });
        }
      );

      if (!this.route.parent) {
        this.renewalsJanusSvc.setData({
          ...this.renewalsJanusSvc.getData(),
          reRunJanusApplicationDataTable: true
        });
      }
    });

    this.renewalsStateSub = this.renewalsJanusSvc.data$.subscribe(data => {
      this.filterInputs = data?.filterInputs || [];

      if (data?.reRunJanusApplicationDataTable) {
        this.fetchRenewals(this.currentPage || 0);

        this.renewalsJanusSvc.setData({
          ...data,
          reRunJanusApplicationDataTable: false
        });
      }
    });

    this.dataSub = this.renewalsJanusSvc.data$.subscribe(data => {
      this.columnOptions = data.columnOptions || [];
      this.exportOptions = data.exportOptions || [];
    });
  }

  ngAfterViewInit(): void {
    this.syncSearchQueryToFilterBuilder();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.dataSub?.unsubscribe();
    this.departmentRouteSub?.unsubscribe();
    this.renewalsStateSub?.unsubscribe();
    this.ref.detach();
  }

  fetchRenewals(page: number) {
    this.currentPage = page;
    this.isLoading = true;
    this.detectChanges();

    const serviceData = this.renewalsJanusSvc.getData();

    let filters: ItemSearchOptionField[] = this.cloneFilterInputs([
      ...(serviceData?.searchText ?? []),
      ...(serviceData?.filterInputs ?? []),
      ...(serviceData?.attributeFilterInputs ?? [])
    ]);

    if (
      this.context.staffDepartmentId &&
      !filters.some(f => f?.title === 'departmentId')
    ) {
      filters.push(
        new ItemSearchOptionField({
          title: 'departmentId',
          options: [
            new ItemSearchOptionFieldOption({
              title: 'departmentId',
              searchOptionMethod: 'dynamicFilter',
              searchText: this.context.staffDepartmentId,
              filterText: '',
              selected: true,
              strOperator: 'is'
            })
          ]
        })
      );
    }

    filters = filters.filter(f => {
      const opt: any = f?.options?.[0];
      if (!opt) return false;

      return (
        (opt.searchText && opt.searchText !== '') ||
        (opt.filterText && opt.filterText.trim() !== '') ||
        opt.customValue?.start || opt.customValue?.end
      );
    });

    // filters = filters.map(f => {
    //   const opt: any = f.options?.[0];

    //   if (opt?.filterText && !opt.searchText) {
    //     opt.searchText = opt.filterText;
    //     opt.filterText = '';
    //   }

    //   if (f.title === 'expiresOn') {
    //     opt.strOperator = '<';
    //   }

    //   return f;
    // });
    filters = filters.map(f => {
      const opt: any = f.options?.[0];

      if (opt?.filterText && !opt.searchText) {
        opt.searchText = opt.filterText;
        opt.filterText = '';
      }

      return f;
    });

    // Renewals About To Expire:
    // Include only registrations expiring from today through the next 7 days.
    const expiresOnFilter = filters.find(f => f.title === '<expiresOn');

    if (expiresOnFilter) {
      const opt: any = expiresOnFilter.options?.[0];

      if (opt?.searchText === this.getSevenDaysFromNow()) {
        const today = new Date();

        const todayString =
          today.getMonth() +
          1 +
          '/' +
          today.getDate() +
          '/' +
          today.getFullYear();

        // Keep the existing <expiresOn filter.
        // Backend already understands <expiresOn as the upper-bound filter.

        // Add a separate lower-bound filter.
        filters.push(
          new ItemSearchOptionField({
            title: 'expiresOn',
            options: [
              new ItemSearchOptionFieldOption({
                title: 'expiresOn',
                searchText: todayString,
                filterText: '',
                selected: true,
                strOperator: '>='
              })
            ]
          })
        );
      }
    }

    this.filterOptions = filters;

    this.workflowSvc
      .searchRegistrationsPaged(
        this.context.client ? this.context.client.id : null,
        this.sortField || 'registeredOn',
        this.sortDescending,
        this.filterOptions,
        this.currentPage,
        this.pageSize
      )
      .subscribe(result => {
        this.totalRecords = result.recordCount ?? 0;
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
        const mappedRenewals = (result.registrations ?? []).map(r => ({
          appDescription: r.appDescription ?? '',
          workflowName: r.workflowName ?? '',
          registrationInfo: {
            name: r.appDescription ?? '',
            business: r.workflowName ?? ''
          },
          type: r.workflowName ?? '',
          registeredOn: r.registeredOnString ?? '',
          effectiveOn: r.effectiveOnString ?? '',
          expiresOn: r.expiresOnString ?? '',
          status: this.REG_STATUS[r.status] ?? String(r.status),
          id: r.id,
          createdApplicationId: r.registerApplicationId
        }));

        if (this.shouldDeferDeepLinkRender(mappedRenewals)) {
          this.resolveDeepLinkPageIfNeeded();
          return;
        }

        this.applyRenewalsResult(mappedRenewals);
        this.resolveDeepLinkPageIfNeeded();
        this.isLoading = false;
        this.isSorting = false;
      });
  }

  private applyRenewalsResult(renewals: any[]): void {
    this.renewals = renewals;

    for (const ac of this.columns) {
      if (!this.columnOptions.find(co => co.name === (ac.key as string))) {
        const newOption = new ColumnOption({
          label: ac.label ? ac.label : ac.key,
          name: ac.key as string,
          includeInExport: true
        });

        this.columnOptions.push(newOption);
      }
    }

    this.exportOptions = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);

    this.renewalsJanusSvc.setData({
      ...this.renewalsJanusSvc.getData(),
      columnOptions: this.columnOptions,
      exportOptions: this.exportOptions
    });
  }

  private shouldDeferDeepLinkRender(renewals: any[]): boolean {
    const targetRegistrationId = this.pendingDeepLinkRegistrationId;

    if (
      !this.shouldResolveDeepLinkBeforeRender ||
      !targetRegistrationId ||
      !this.pendingDeepLinkView ||
      this.resolvingDeepLinkPage ||
      this.failedDeepLinkRegistrationId === targetRegistrationId ||
      this.resolvedDeepLinkRegistrationId === targetRegistrationId
    ) {
      return false;
    }

    return (
      !renewals.some(renewal => renewal?.id === targetRegistrationId) &&
      this.totalPages > 1
    );
  }

  private detectChanges() {
    if (!this.isDestroyed) this.ref.detectChanges();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.syncSharedStateToRoute();
    this.fetchRenewals(page);
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.currentPage = 0;
    this.syncSharedStateToRoute();
    this.fetchRenewals(this.currentPage);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.detectChanges();
  }

  toggleAttributeMenu(ev?: Event) {
    ev?.stopPropagation();
    this.closeAllDropdowns();
    this.showAttributeOptions = !this.showAttributeOptions;
  }

  toggleMyWorkMenu(ev?: Event) {
    ev?.stopPropagation();
    this.closeAllDropdowns();
    this.showMyWorkOptions = !this.showMyWorkOptions;
  }

  onAttribute(value: string) {}

  onMyWork(value: string) {}

  onBulk(value: string) {}

  openFilters(ev?: Event) {
    ev?.stopPropagation();
  }

  toggleExportMenu(ev?: Event) {
    ev?.stopPropagation();
    this.closeAllDropdowns();
    this.showExportOptions = !this.showExportOptions;
  }

  closeAllDropdowns() {
    this.showDeptOptions = false;
    this.showAttributeOptions = false;
    this.showMyWorkOptions = false;
    this.showExportOptions = false;
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.closeAllDropdowns();
  }

  receiveColumns(cols: TableColumn[]) {
    this.columns = cols;
    this.updateApplicationAttributesFromColumns();
  }

  onRenewalDisplayedColumnsChange(displayedColumns: string[]): void {
    this.visibleRenewalColumnKeys = displayedColumns.filter(
      columnKey => columnKey !== 'action'
    );
    const visibleColumnSet = new Set(displayedColumns);

    this.columnOptions = this.columnOptions.map(option => ({
      ...option,
      checked: visibleColumnSet.has(option.name)
    }));
    this.updateApplicationAttributesFromColumns();
  }

  // Keep the Attribute menu aligned with the renewal table's filterable
  // headers, including their exact labels and left-to-right order.
  private updateApplicationAttributesFromColumns(): void {
    const fallbackColumns: TableColumn[] = [
      { key: 'appDescription', label: 'Registration Info' },
      { key: 'workflowName', label: 'App Type' },
      { key: 'registeredOn', label: 'Registered On' },
      { key: 'effectiveOn', label: 'Effective On' },
      { key: 'expiresOn', label: 'Expires On' },
      { key: 'status', label: 'Status' }
    ];
    let sourceColumns = this.columns?.length
      ? this.columns.filter(column => column.key !== 'action')
      : fallbackColumns;

    if (this.visibleRenewalColumnKeys) {
      const visibleColumnSet = new Set(this.visibleRenewalColumnKeys);
      sourceColumns = sourceColumns.filter(column =>
        visibleColumnSet.has(column.key)
      );
    }
    const configMap: Record<string, Partial<FilterBuilderParam>> = {
      appDescription: {
        types: ['contains', 'is']
      },
      workflowName: {
        id: 'renewalType',
        types: ['is', 'in'],
        options: [...this.workflowOpts]
      },
      registeredOn: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      effectiveOn: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      expiresOn: {
        types: ['is', 'range'],
        inputType: 'date'
      },
      status: {
        types: ['is', 'in'],
        options: [
          { name: 'Pending', value: 'Pending' },
          { name: 'Approved', value: 'Approved' },
          { name: 'Denied', value: 'Denied' },
          { name: 'Expired', value: 'Expired' },
          { name: 'Inactive', value: 'Inactive' },
          { name: 'Deleted', value: 'Deleted' },
          { name: 'Renewed', value: 'Renewed' },
          { name: 'Discarded', value: 'Discarded' }
        ]
      }
    };

    this.applicationAttributes = sourceColumns.map(column => {
      const config = configMap[column.key] || {};

      return {
        id: config.id || column.key,
        name: column.label || column.key,
        types: config.types || ['contains', 'is'],
        inputType: config.inputType,
        options: config.options
      };
    });
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const sortDescending = this.sort.dir === 'desc' ? true : false;

    let filters = [...this.filterOptions];
    if (this.staticFilters) {
      filters = [...this.filterOptions, ...this.staticFilters];
    }

    return this.workflowSvc
      .exportRegistrations(
        this.context.client ? this.context.client.id : null,
        this.useExcelFormat,
        this.exportOptions,
        this.sort.prop,
        sortDescending,
        filters,
        this.page.pageNumber,
        this.page.size
      )
      .subscribe(
        data => {
          if (this.useExcelFormat) {
            const blob = new Blob([data], {
              type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `renewals-export-${new Date().getTime()}.xlsx`;
            link.click();
          } else {
            const blob = new Blob([data], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `renewals-export-${new Date().getTime()}.csv`;
            link.click();
          }
        },
        err => {
          this._toastrSvc.error('Problem while downloading the file.');
          console.error(err);
        }
      );
  }

  private applySharedState(
    sharedState: RenewalsSharedState,
    reRun: boolean
  ): void {
    const normalizedState = this.normalizeSharedState(sharedState);

    this.pendingSharedState = normalizedState;
    this.filterInputs = normalizedState.filterInputs;
    this.searchQuery =
      normalizedState.searchQuery ||
      this.getSearchQuery(normalizedState.searchText);
    this.syncSearchQueryToFilterBuilder();
    this.sortField = normalizedState.sortField;
    this.sortDescending = normalizedState.sortDescending;
    this.persistSharedState(normalizedState);

    this.renewalsJanusSvc.setData({
      ...this.renewalsJanusSvc.getData(),
      searchText: normalizedState.searchText,
      filterInputs: normalizedState.filterInputs,
      attributeFilterInputs: normalizedState.attributeFilterInputs,
      reRunJanusApplicationDataTable: reRun
    });
  }

  private syncSharedStateToRoute(): void {
    const sharedState = this.normalizeSharedState({
      searchText: this.renewalsJanusSvc.getData()?.searchText ?? [],
      filterInputs: this.renewalsJanusSvc.getData()?.filterInputs ?? [],
      attributeFilterInputs:
        this.renewalsJanusSvc.getData()?.attributeFilterInputs ?? [],
      sortField: this.sortField ?? this.DEFAULT_SORT_FIELD,
      sortDescending: this.sortDescending ?? this.DEFAULT_SORT_DESCENDING,
      searchQuery: this.searchQuery
    });
    const queryParams = this.buildSharedStateQueryParams(sharedState);

    if (this.hasSharedState(sharedState)) {
      this.persistSharedState(sharedState);
    } else {
      localStorage.removeItem(this.FILTER_CACHE_KEY);
      localStorage.removeItem(this.LEGACY_FILTER_CACHE_KEY);
    }

    this.lastSharedStateParam = this.getRouteStateSignatureFromQueryParams(
      queryParams
    );
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        p: this.currentPage + 1,
        ...queryParams
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private parseRouteSharedState(params: ParamMap): RenewalsSharedState | null {
    const searchQuery = params.get(this.SEARCH_QUERY_PARAM) ?? '';
    const attributeTokens = params.getAll(this.ATTRIBUTE_FILTER_QUERY_PARAM);
    const externalTokens = params.getAll(this.EXTERNAL_FILTER_QUERY_PARAM);
    const sortField = params.get(this.SORT_FIELD_QUERY_PARAM);
    const sortDesc = params.get(this.SORT_DESC_QUERY_PARAM);
    const hasReadableState =
      !!searchQuery ||
      attributeTokens.length > 0 ||
      externalTokens.length > 0 ||
      sortField != null ||
      sortDesc != null;

    if (hasReadableState) {
      return this.normalizeSharedState({
        searchQuery,
        filterInputs: this.parseFilterTokens(externalTokens, 'external'),
        attributeFilterInputs: this.parseFilterTokens(
          attributeTokens,
          'attribute'
        ),
        sortField: sortField ?? this.DEFAULT_SORT_FIELD,
        sortDescending:
          sortDesc == null ? this.DEFAULT_SORT_DESCENDING : sortDesc !== '0'
      });
    }

    return this.parseSharedState(params.get(this.SHARED_STATE_QUERY_PARAM));
  }

  private buildSharedStateQueryParams(
    sharedState: RenewalsSharedState | null
  ): Record<string, string | string[] | null> {
    if (!sharedState || !this.hasSharedState(sharedState)) {
      return {
        [this.SHARED_STATE_QUERY_PARAM]: null,
        [this.SEARCH_QUERY_PARAM]: null,
        [this.ATTRIBUTE_FILTER_QUERY_PARAM]: null,
        [this.EXTERNAL_FILTER_QUERY_PARAM]: null,
        [this.SORT_FIELD_QUERY_PARAM]: null,
        [this.SORT_DESC_QUERY_PARAM]: null
      };
    }

    return {
      [this.SHARED_STATE_QUERY_PARAM]: null,
      [this.SEARCH_QUERY_PARAM]: sharedState.searchQuery || null,
      [this.ATTRIBUTE_FILTER_QUERY_PARAM]: this.serializeFilterTokens(
        sharedState.attributeFilterInputs,
        'attribute'
      ),
      [this.EXTERNAL_FILTER_QUERY_PARAM]: this.serializeFilterTokens(
        sharedState.filterInputs,
        'external'
      ),
      [this.SORT_FIELD_QUERY_PARAM]:
        sharedState.sortField !== this.DEFAULT_SORT_FIELD
          ? sharedState.sortField || this.DEFAULT_SORT_FIELD
          : null,
      [this.SORT_DESC_QUERY_PARAM]:
        sharedState.sortDescending !== this.DEFAULT_SORT_DESCENDING
          ? sharedState.sortDescending
            ? '1'
            : '0'
          : null
    };
  }

  private shouldNormalizeSharedStateParams(
    params: ParamMap,
    normalizedQueryParams: Record<string, string | string[] | null>,
    legacySharedStateParam: string | null
  ): boolean {
    if (legacySharedStateParam) {
      return true;
    }

    return (
      (params.get(this.SEARCH_QUERY_PARAM) ?? null) !==
        (normalizedQueryParams[this.SEARCH_QUERY_PARAM] ?? null) ||
      JSON.stringify(params.getAll(this.ATTRIBUTE_FILTER_QUERY_PARAM)) !==
        JSON.stringify(
          normalizedQueryParams[this.ATTRIBUTE_FILTER_QUERY_PARAM] ?? []
        ) ||
      JSON.stringify(params.getAll(this.EXTERNAL_FILTER_QUERY_PARAM)) !==
        JSON.stringify(
          normalizedQueryParams[this.EXTERNAL_FILTER_QUERY_PARAM] ?? []
        ) ||
      (params.get(this.SORT_FIELD_QUERY_PARAM) ?? null) !==
        (normalizedQueryParams[this.SORT_FIELD_QUERY_PARAM] ?? null) ||
      (params.get(this.SORT_DESC_QUERY_PARAM) ?? null) !==
        (normalizedQueryParams[this.SORT_DESC_QUERY_PARAM] ?? null)
    );
  }

  private getRouteStateSignature(params: ParamMap): string {
    return JSON.stringify({
      q: params.get(this.SEARCH_QUERY_PARAM) ?? null,
      af: params.getAll(this.ATTRIBUTE_FILTER_QUERY_PARAM),
      ef: params.getAll(this.EXTERNAL_FILTER_QUERY_PARAM),
      sf: params.get(this.SORT_FIELD_QUERY_PARAM) ?? null,
      sd: params.get(this.SORT_DESC_QUERY_PARAM) ?? null,
      rs: params.get(this.SHARED_STATE_QUERY_PARAM) ?? null
    });
  }

  private getRouteStateSignatureFromQueryParams(
    queryParams: Record<string, string | string[] | null>
  ): string {
    return JSON.stringify({
      q: queryParams[this.SEARCH_QUERY_PARAM] ?? null,
      af: queryParams[this.ATTRIBUTE_FILTER_QUERY_PARAM] ?? [],
      ef: queryParams[this.EXTERNAL_FILTER_QUERY_PARAM] ?? [],
      sf: queryParams[this.SORT_FIELD_QUERY_PARAM] ?? null,
      sd: queryParams[this.SORT_DESC_QUERY_PARAM] ?? null,
      rs: queryParams[this.SHARED_STATE_QUERY_PARAM] ?? null
    });
  }

  private parseSharedState(
    rawState: string | null
  ): RenewalsSharedState | null {
    if (!rawState) {
      return null;
    }

    try {
      return this.normalizeSharedState(JSON.parse(rawState));
    } catch (_legacyError) {
      try {
        const decodedState = this.decodeBase64Url(rawState);
        const parsedState = JSON.parse(
          decodedState
        ) as RenewalsSharedRouteState;

        return this.normalizeSharedState({
          searchQuery: parsedState.q ?? '',
          filterInputs: this.fromCompactFilters(parsedState.f ?? []),
          attributeFilterInputs: this.fromCompactFilters(parsedState.a ?? []),
          sortField: parsedState.sf ?? this.DEFAULT_SORT_FIELD,
          sortDescending:
            parsedState.sd == null
              ? this.DEFAULT_SORT_DESCENDING
              : parsedState.sd === 1
        });
      } catch (_compactError) {
        return null;
      }
    }
  }

  private normalizeSharedState(
    state: Partial<RenewalsSharedState>
  ): RenewalsSharedState {
    const searchQuery =
      state?.searchQuery ?? this.getSearchQuery(state?.searchText ?? []);
    const searchText =
      (state?.searchText?.length ?? 0) > 0
        ? this.cloneFilterInputs(state?.searchText ?? [])
        : searchQuery && this.applicationAttributes.length > 0
        ? this.buildSearchFilters(searchQuery)
        : [];

    return {
      searchText,
      filterInputs: this.cloneFilterInputs(state?.filterInputs ?? []),
      attributeFilterInputs: this.cloneFilterInputs(
        state?.attributeFilterInputs ?? []
      ),
      sortField: state?.sortField ?? this.DEFAULT_SORT_FIELD,
      sortDescending:
        typeof state?.sortDescending === 'boolean'
          ? state.sortDescending
          : this.DEFAULT_SORT_DESCENDING,
      searchQuery
    };
  }

  private persistSharedState(sharedState: RenewalsSharedState): void {
    localStorage.setItem(this.FILTER_CACHE_KEY, JSON.stringify(sharedState));

    if (sharedState.filterInputs.length > 0) {
      localStorage.setItem(
        this.LEGACY_FILTER_CACHE_KEY,
        JSON.stringify(sharedState.filterInputs)
      );
    } else {
      localStorage.removeItem(this.LEGACY_FILTER_CACHE_KEY);
    }
  }

  private hasSharedState(sharedState: RenewalsSharedState): boolean {
    return (
      sharedState.searchText.length > 0 ||
      !!sharedState.searchQuery ||
      sharedState.filterInputs.length > 0 ||
      sharedState.attributeFilterInputs.length > 0 ||
      sharedState.sortField !== this.DEFAULT_SORT_FIELD ||
      sharedState.sortDescending !== this.DEFAULT_SORT_DESCENDING
    );
  }

  private getSearchQuery(searchText: ItemSearchOptionField[]): string {
    const firstOption = searchText?.[0]?.options?.[0];

    return firstOption?.filterText || firstOption?.searchText || '';
  }

  private buildSearchFilters(searchQuery: string): ItemSearchOptionField[] {
    return (this.applicationAttributes || []).map(attribute => ({
      title: attribute?.id,
      options: [
        new ItemSearchOptionFieldOption({
          title: attribute?.id,
          searchOptionMethod: 'simpleSearch',
          filterText: searchQuery
        })
      ]
    }));
  }

  private serializeFilterTokens(
    filters: ItemSearchOptionField[] = [],
    mode: 'attribute' | 'external'
  ): string[] | null {
    const tokens = this.cloneFilterInputs(filters)
      .flatMap(filter =>
        (filter.options || []).map(option => {
          const value =
            mode === 'attribute'
              ? option?.filterText || option?.searchText || ''
              : option?.searchText || option?.filterText || '';

          return [
            encodeURIComponent(filter.title || ''),
            encodeURIComponent(option?.strOperator || ''),
            encodeURIComponent(value)
          ].join(':');
        })
      )
      .filter(token => token !== '::');

    return tokens.length > 0 ? tokens : null;
  }

  private parseFilterTokens(
    tokens: string[] = [],
    mode: 'attribute' | 'external'
  ): ItemSearchOptionField[] {
    const grouped = new Map<string, ItemSearchOptionFieldOption[]>();

    tokens.forEach(token => {
      const [rawTitle = '', rawOperator = '', ...rawValueParts] = token.split(
        ':'
      );
      const title = decodeURIComponent(rawTitle || '');
      const operator = decodeURIComponent(rawOperator || '');
      const value = decodeURIComponent(rawValueParts.join(':') || '');

      if (!title) {
        return;
      }

      const option = new ItemSearchOptionFieldOption({
        title,
        selected: true,
        filterText: mode === 'attribute' ? value : '',
        searchText: mode === 'external' ? value : '',
        strOperator: operator
      });

      if (!grouped.has(title)) {
        grouped.set(title, []);
      }

      grouped.get(title)?.push(option);
    });

    return Array.from(grouped.entries()).map(
      ([title, options]) =>
        new ItemSearchOptionField({
          title,
          options
        })
    );
  }

  private fromCompactFilters(
    filters: RenewalsSharedRouteFilter[] = []
  ): ItemSearchOptionField[] {
    return (filters || []).map(
      filter =>
        new ItemSearchOptionField({
          title: filter.t,
          options: (filter.o || []).map(
            option =>
              new ItemSearchOptionFieldOption({
                title: option?.t || filter.t,
                selected: true,
                filterText: option?.f ?? '',
                searchText: option?.s ?? '',
                strOperator: option?.op ?? '',
                searchOptionMethod: option?.m,
                inputType: option?.i,
                ...(option?.c ? ({ customValue: option.c } as any) : {})
              } as any)
          )
        })
    );
  }

  private decodeBase64Url(value: string): string {
    const padding = '='.repeat((4 - (value.length % 4)) % 4);
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/') + padding;
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

    return new TextDecoder().decode(bytes);
  }

  private cloneFilterInputs(
    filters: ItemSearchOptionField[] = []
  ): ItemSearchOptionField[] {
    return JSON.parse(JSON.stringify(filters || []));
  }

  private syncSearchQueryToFilterBuilder(): void {
    if (!this.filterBuilderComponent) {
      return;
    }

    this.filterBuilderComponent.searchQuery = this.searchQuery || '';
    this.detectChanges();
  }

  private reloadTableWhenChipShows(): void {
    setTimeout(() => {
      this.detectChanges();
    });
  }

  private resolveDeepLinkPageIfNeeded(): void {
    const targetRegistrationId = this.pendingDeepLinkRegistrationId;

    if (
      !targetRegistrationId ||
      !this.pendingDeepLinkView ||
      this.resolvingDeepLinkPage ||
      this.failedDeepLinkRegistrationId === targetRegistrationId
    ) {
      return;
    }

    if (this.renewals.some(renewal => renewal?.id === targetRegistrationId)) {
      this.resolvedDeepLinkRegistrationId = targetRegistrationId;
      this.failedDeepLinkRegistrationId = null;
      this.shouldResolveDeepLinkBeforeRender = false;
      return;
    }

    if (
      this.resolvedDeepLinkRegistrationId === targetRegistrationId ||
      this.totalPages <= 1
    ) {
      return;
    }

    const pagesToCheck: number[] = [];

    for (let pageIndex = 0; pageIndex < this.totalPages; pageIndex++) {
      if (pageIndex !== this.currentPage) {
        pagesToCheck.push(pageIndex);
      }
    }

    if (pagesToCheck.length === 0) {
      this.failedDeepLinkRegistrationId = targetRegistrationId;
      return;
    }

    this.resolvingDeepLinkPage = true;
    this.findDeepLinkPage(targetRegistrationId, pagesToCheck, [
      ...this.filterOptions
    ]);
  }

  private findDeepLinkPage(
    targetRegistrationId: string,
    pagesToCheck: number[],
    filters: ItemSearchOptionField[]
  ): void {
    // Added by Amisha
    // Reason: Copied non-Janus renewal detail links may point to registrations on page 2/3, so we locate the correct Janus page first.
    const nextPage = pagesToCheck.shift();

    if (nextPage == null) {
      this.resolvingDeepLinkPage = false;
      this.failedDeepLinkRegistrationId = targetRegistrationId;
      return;
    }

    this.workflowSvc
      .searchRegistrationsPaged(
        this.context.client ? this.context.client.id : null,
        this.sortField || this.DEFAULT_SORT_FIELD,
        this.sortDescending,
        filters,
        nextPage,
        this.pageSize
      )
      .subscribe({
        next: result => {
          const found = (result.registrations ?? []).some(
            registration => registration?.id === targetRegistrationId
          );

          if (found) {
            this.resolvingDeepLinkPage = false;
            this.resolvedDeepLinkRegistrationId = targetRegistrationId;
            this.failedDeepLinkRegistrationId = null;
            this.shouldResolveDeepLinkBeforeRender = false;
            this.currentPage = nextPage;
            const mappedRenewals = (result.registrations ?? []).map(r => ({
              appDescription: r.appDescription ?? '',
              workflowName: r.workflowName ?? '',
              registrationInfo: {
                name: r.appDescription ?? '',
                business: r.workflowName ?? ''
              },
              type: r.workflowName ?? '',
              registeredOn: r.registeredOnString ?? '',
              effectiveOn: r.effectiveOnString ?? '',
              expiresOn: r.expiresOnString ?? '',
              status: this.REG_STATUS[r.status] ?? String(r.status),
              id: r.id,
              createdApplicationId: r.registerApplicationId
            }));

            this.applyRenewalsResult(mappedRenewals);
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {
                p: nextPage + 1
              },
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
            this.isLoading = false;
            this.isSorting = false;
            return;
          }

          this.findDeepLinkPage(targetRegistrationId, pagesToCheck, filters);
        },
        error: () => {
          this.resolvingDeepLinkPage = false;
          this.shouldResolveDeepLinkBeforeRender = false;
          this.isLoading = false;
        }
      });
  }
}
